import { Button, Dropdown, Tooltip, notification } from 'antd';
import type { MenuProps } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { LuSearch } from 'react-icons/lu';
import { TextM, TextS } from 'components/Text';
import {
	WalletCharge,
	WalletChargeFilters,
	WalletChargeStatus,
} from 'types/Wallet';
import { useCallback, useState } from 'react';
import { Table } from 'components/Table';
import { StyledFiltersContainer } from './styles';
import { FilterButton } from 'components/FilterButton';
import { AiOutlineStop } from 'react-icons/ai';
import { useTheme } from 'styled-components';
import { formatCurrency, normalizeCpfCnpj } from 'helpers/normalizers';
import { ESortingPage, PaginationInfo } from 'types/List';
import { WalletsService } from 'modules/debitCollections/services';
import { WalletChargeStatusDescription } from 'constants/wallet';
import dayjs from 'dayjs';
import { ExtendExpiration } from 'modules/debitCollections/components/ExtendExpiration';
import { Discount } from 'modules/debitCollections/components/Discount';
import { DebtorAddress } from 'modules/debitCollections/components/DebtorAddress';
import { InterestFine } from 'modules/debitCollections/components/InterestFine';
import { useCharges } from 'modules/debitCollections/hooks/useCharges';
import ChargesFilters from './ChargesFilters';
import { IoMdInformationCircleOutline } from 'react-icons/io';
import { formatAddZerosMaskInput } from 'helpers/formaters';
import { sortTableHandler } from 'helpers/tables';
import CancelChargeModal from '../Charges/CancelChargeModal';
import { BsArrowBarDown } from 'react-icons/bs';
import FileSaver from 'file-saver';

const ChargesBarcodes = () => {
	const theme = useTheme();
	const [api, contextHolder] = notification.useNotification();

	const [paginationInfo, setPaginationInfo] = useState<PaginationInfo>({
		currentPage: 1,
		pageSize: 10,
	});
	const [sorting, setSorting] = useState<{
		order: ESortingPage;
		sort: string;
	}>({ order: ESortingPage.DESC, sort: 'created_at' });

	const [isFiltersOpen, setIsFiltersOpen] = useState<boolean>(false);
	const [isExtendExpirationOpen, setIsExtendExpirationOpen] = useState<
		WalletCharge | false
	>(false);
	const [isDiscountOpen, setIsDiscountOpen] = useState<WalletCharge | false>(
		false,
	);
	const [isDebtorAddressOpen, setIsDebtorAddressOpen] = useState<
		WalletCharge | false
	>(false);
	const [isInterestFineOpen, setIsInterestFineOpen] = useState<
		WalletCharge | false
	>(false);

	const [cancelChargeItem, setCancelChargeItem] = useState<WalletCharge>();
	const [filters, setFilters] = useState<WalletChargeFilters>(
		{} as WalletChargeFilters,
	);

	const handleClearFilters = useCallback(() => {
		setFilters({} as WalletChargeFilters);
	}, []);

	const { data, isLoading } = useQuery({
		queryKey: ['mainChargesList', paginationInfo, filters, sorting],
		queryFn: () =>
			WalletsService.getAllCharges({
				...filters,
				page: paginationInfo.currentPage,
				size: paginationInfo.pageSize,
				order: sorting.order,
				sort: sorting.sort,
			}),
	});

	const { getChargePdf, isChargePdfLoading } = useCharges({
		onError: e => {
			api.error({
				description: e.data.message,
				message: 'Ocorreu um problema ao buscar as cobranças.',
			});
		},
		onChargeDownload: chargePdf => {
			window.open(chargePdf, '_blank');
		},
	});

	const { getChargesExportFile } = useCharges({
		onBlobDownload: blob => {
			if (!blob) {
				api.error({
					description: 'Erro ao baixar o arquivo.',
					message: 'Ocorreu um problema ao baixar o arquivo.',
				});
				return;
			}

			const fileName = `cobrancas_${dayjs().format(
				'YYYY-MM-DD_HH-mm-ss',
			)}.txt`;

			const url = window.URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = fileName;
			document.body.appendChild(a);
			a.click();
			a.remove();
			window.URL.revokeObjectURL(url);
		},
		onError: e => {
			api.error({
				description: e.data.message,
				message: 'Ocorreu um problema ao baixar o arquivo.',
			});
		},
	});

	const { cancelCharge } = useCharges({
		onError: e => {
			api.error({
				description: e.data.message,
				message: 'Ocorreu um problema ao cancelar boleto.',
			});
		},
		onCancelChargeSuccess: () => {
			api.success({
				message: 'Boleto cancelado com sucesso.',
			});
		},
	});

	const createActionItems = (item: WalletCharge): MenuProps['items'] => {
		const defaultItems: MenuProps['items'] = [];

		if (item.status === WalletChargeStatus.ERROR) {
			defaultItems.push(
				{
					label: 'Prorrogar',
					key: '2',
					onClick: () => {
						setIsExtendExpirationOpen(item);
					},
				},
				{
					label: 'Desconto',
					key: '3',
					onClick: () => {
						setIsDiscountOpen(item);
					},
				},
				{
					label: 'Adicionar / Editar endereço do pagador',
					key: '4',
					onClick: () => {
						setIsDebtorAddressOpen(item);
					},
				},
				{
					label: 'Editar Multa e Juros',
					key: '5',
					onClick: () => {
						setIsInterestFineOpen(item);
					},
				},
			);
		} else if (item.status === WalletChargeStatus.PENDING) {
			defaultItems.push({
				label: 'Baixar boleto',
				key: '1',
				onClick: () => {
					getChargePdf(item?.wallet_id || '', item.id);
				},
			});

			defaultItems.push({
				label: 'Cancelar boleto',
				key: '6',
				onClick: () => {
					setCancelChargeItem(item);
				},
			});
		}
		return defaultItems;
	};

	const columns: ColumnsType<WalletCharge> = [
		{
			title: 'Cód. do boleto',
			width: '23%',
			render: (charge: WalletCharge) => (
				<TextM weight="normal" style={{ textTransform: 'uppercase' }}>
					{typeof charge.charge_code === 'number'
						? `${formatAddZerosMaskInput(String(charge.charge_code))}`
						: '-'}
				</TextM>
			),
		},
		{
			title: 'Sacado',
			render: (charge: WalletCharge) => (
				<TextM weight="normal">{`${charge.debtor?.name}`}</TextM>
			),
			width: '15%',
		},
		{
			title: 'CPF / CNPJ',
			width: '15%',
			render: (charge: WalletCharge) => (
				<TextM weight="normal">
					{charge.debtor?.document
						? normalizeCpfCnpj(charge.debtor?.document)
						: '-'}
				</TextM>
			),
		},
		{
			title: 'Data de vencimento',
			width: '15%',
			render: (charge: WalletCharge) => (
				<TextM weight="normal">
					{dayjs(charge.due_date?.split('T')?.[0]).format(
						'DD/MM/YYYY',
					)}
				</TextM>
			),
		},
		{
			title: 'Valor',
			width: '10%',
			render: (charge: WalletCharge) => (
				<TextM weight="normal">{formatCurrency(charge.amount)}</TextM>
			),
		},
		{
			title: 'Status',
			width: '10%',
			render: (charge: WalletCharge) => {
				if (
					typeof charge.error === 'string' &&
					[WalletChargeStatus.ERROR].includes(charge.status)
				)
					return (
						<Tooltip title={charge.error}>
							<div
								style={{
									display: 'flex',
									alignItems: 'center',
									gap: '8px',
								}}
							>
								<TextM weight="normal">
									{
										WalletChargeStatusDescription[
											charge.status
										]
									}
								</TextM>
								<IoMdInformationCircleOutline size={16} />
							</div>
						</Tooltip>
					);

				return (
					<TextM weight="normal">
						{WalletChargeStatusDescription[charge.status]}
					</TextM>
				);
			},
		},
		{
			key: 'action',
			width: '20%',
			align: 'end',
			render: (charge: WalletCharge) => {
				const isDisabled = ![
					WalletChargeStatus.ERROR,
					WalletChargeStatus.PENDING,
				].includes(charge.status);
				return (
					<Dropdown
						menu={{ items: createActionItems(charge) }}
						disabled={isDisabled}
						trigger={['click']}
					>
						<Button
							type="link"
							loading={isChargePdfLoading === charge.id}
							disabled={isDisabled}
							style={{
								color: theme.text,
								fontWeight: 'bold',
								fontSize: '1.3rem',
								lineHeight: '1rem',
								letterSpacing: '0.09rem',
								opacity: isDisabled ? '0.4' : '1',
							}}
						>
							...
						</Button>
					</Dropdown>
				);
			},
		},
	];

	return (
		<>
			{contextHolder}
			<StyledFiltersContainer>
				<FilterButton
					icon={<LuSearch size={18} />}
					onClick={() => setIsFiltersOpen(true)}
				>
					<TextS style={{ color: theme.white }}>Busca avançada</TextS>
				</FilterButton>
				<FilterButton
					icon={<AiOutlineStop size={18} color={theme.white} />}
					variation="secondary"
					onClick={handleClearFilters}
					disabled={Object.keys(filters).length === 0}
				>
					<TextS style={{ color: theme.white }}>Limpar filtros</TextS>
				</FilterButton>

				<Button
					style={{
						color: theme.primary,
					}}
					type="default"
					onClick={async () => {
						await getChargesExportFile({
							...filters,
							page: paginationInfo.currentPage,
							size: paginationInfo.pageSize,
							order: sorting.order,
							sort: sorting.sort,
						});
					}}
				>
					Exportar cobranças
					<BsArrowBarDown
						style={{ marginLeft: '0.6rem' }}
						size={18}
					/>
				</Button>
			</StyledFiltersContainer>
			<Table
				columns={columns}
				rowKey={record => record.id}
				dataSource={data?.content}
				loading={isLoading}
				onChange={(_, __, sorter) =>
					sortTableHandler(sorter, setSorting)
				}
				pagination={{
					total: data?.total_elements || 0,
					showTotal(total) {
						const currentSize =
							paginationInfo.currentPage *
							paginationInfo.pageSize;
						return `${currentSize > total ? total : currentSize} de ${total}`;
					},
					pageSizeOptions: ['10', '30', '60', '90'],
					showSizeChanger: true,
					pageSize: paginationInfo.pageSize,
					current: paginationInfo.currentPage,
					onChange(page, pageSize) {
						setPaginationInfo({
							currentPage: page,
							pageSize,
						});
					},
				}}
			/>
			<ExtendExpiration
				isOpen={isExtendExpirationOpen !== false}
				walletId={
					typeof isExtendExpirationOpen === 'object' &&
					isExtendExpirationOpen != null
						? isExtendExpirationOpen?.wallet_id || ''
						: ''
				}
				charge={isExtendExpirationOpen as WalletCharge}
				onClose={() => setIsExtendExpirationOpen(false)}
			/>
			<Discount
				isOpen={isDiscountOpen !== false}
				walletId={
					typeof isDiscountOpen === 'object' && isDiscountOpen != null
						? isDiscountOpen?.wallet_id || ''
						: ''
				}
				charge={isDiscountOpen as WalletCharge}
				onClose={() => setIsDiscountOpen(false)}
			/>
			<DebtorAddress
				isOpen={isDebtorAddressOpen !== false}
				walletId={
					typeof isDebtorAddressOpen === 'object' &&
					isDebtorAddressOpen != null
						? isDebtorAddressOpen?.wallet_id || ''
						: ''
				}
				charge={isDebtorAddressOpen as WalletCharge}
				onClose={() => setIsDebtorAddressOpen(false)}
			/>
			<InterestFine
				isOpen={isInterestFineOpen !== false}
				walletId={
					typeof isInterestFineOpen === 'object' &&
					isInterestFineOpen != null
						? isInterestFineOpen?.wallet_id || ''
						: ''
				}
				charge={isInterestFineOpen as WalletCharge}
				onClose={() => setIsInterestFineOpen(false)}
			/>
			<ChargesFilters
				isOpen={isFiltersOpen}
				filters={filters}
				onApply={filters => {
					setFilters(filters);
					setIsFiltersOpen(false);
				}}
				onClose={() => setIsFiltersOpen(false)}
			/>
			{!!cancelChargeItem && (
				<CancelChargeModal
					isOpen={!!cancelChargeItem}
					onClose={() => setCancelChargeItem(undefined)}
					walletCharge={cancelChargeItem}
					handleConfirm={comment => {
						cancelCharge(
							cancelChargeItem?.wallet_id || '',
							cancelChargeItem.id,
							comment,
						);
						setCancelChargeItem(undefined);
					}}
				/>
			)}
		</>
	);
};

export default ChargesBarcodes;
